<template>
  <Navbar :title="title" :class="{'show' : showNavbar}">
    <template v-slot:close>
      <div @click="$emit('close')" class="close">
        Cerrar
      </div>
    </template>
    <template v-slot:content>
      <!--Local-->
      <div class="form-group">
        <label for="local">Propiedad</label>
        <select v-model="document.local" class="select" name="local" id="local">
          <option v-for="(e, index) in estates" :value="e" :key="index">{{ e.description }}</option>
        </select>
        <span v-if="this.validationErrors.local" class="error">{{ this.validationErrors.local }}</span>
      </div>
      <!--Type-->
      <div class="form-group">
        <label for="type">Tipo de gasto</label>
        <select v-model="document.type" class="select" name="type" id="type">
          <option v-for="(o, index) in types" :key="index" :value="o">{{ o }}</option>
        </select>
        <span v-if="this.validationErrors.type" class="error">{{ this.validationErrors.type }}</span>
      </div>
      <!--Observation-->
      <div class="form-group">
        <label for="observation">Observación</label>
        <input v-model="document.observation" class="input" type="text" id="observation" name="observation">
        <span v-if="this.validationErrors.observation" class="error">{{ this.validationErrors.name }}</span>
      </div>
      <!--Currency-->
      <div class="form-group">
        <label for="currency">Moneda</label>
        <select v-model="document.currency" class="select" name="currency" id="currency">
          <option v-for="(c, index) in currencies" :value="c" :key="index">{{ c.name }}</option>
        </select>
        <span v-if="this.validationErrors.currency" class="error">{{ this.validationErrors.currency }}</span>
      </div>
      <!--Total-->
      <div class="form-group">
        <label for="rent">Total</label>
        <input v-model.number="document.total" class="input" type="number" id="rent"
               name="rent">
        <span v-if="this.validationErrors.total" class="error">{{ this.validationErrors.total }}</span>
      </div>
      <!--Deadline-->
      <div class="form-group">
        <label for="deadline">Fecha de vencimiento</label>
        <v-date-picker
          ref="deadline"
          id="deadline"
          name="deadline"
          v-model="document.deadline"
          color="red"
          is-expanded
          :timezone="`America/Lima`"
        />
        <span v-if="this.validationErrors.deadline" class="error">{{ this.validationErrors.deadline }}</span>
      </div>
    </template>
    <template v-slot:footer>
      <button :disabled="progress" :class="{'is-loading': progress}" @click="askConfirm" class="btn">
        <span v-if="!progress">Guardar</span>
      </button>
    </template>
  </Navbar>
  <Shadow :class="{'show' : showNavbar}"/>
  <ConfirmDialog
    :show="showConfirm"
    title="Guardar cambios"
    :message="changesMsg"
    confirm-text="Guardar cambios"
    cancel-text="Volver"
    variant="warning"
    :loading="progress"
    @confirm="save"
    @cancel="showConfirm = false"
  />
</template>

<script>
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import ConfirmDialog from '@/components/ConfirmDialog'
import { db, FieldValue, auth } from '@/firebase/firebaseConfig'
import moment from 'moment'
import { EXPENSE_TYPES } from '@/utils/expenseTypes'
import * as yup from 'yup'

const _ = require('lodash')

yup.setLocale({
  mixed: {
    required: 'Este campo es obligatorio'
  },
  number: {
    positive: 'Debe ser positivo',
    integer: 'Debe ser entero',
    truncate: 'Formato incorrecto'
  }
})

export default {
  name: 'UpdateExpense',
  props: {
    showNavbar: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    title: {
      type: String
    },
    data: {
      type: Object,
      required: true
    },
    estates: {
      type: Array,
      required: true
    }
  },
  components: {
    Navbar,
    Shadow,
    ConfirmDialog
  },
  data () {
    return {
      document: {
        client: {
          currency: {
            code: 'PEN'
          }
        }
      },
      schema: yup.object().shape({
        deadline: yup.date().required().label('Vencimiento'),
        total: yup.number().required().positive().label('Total'),
        type: yup.string().required().label('Tipo'),
        currency: yup.object().required().label('Moneda'),
        local: yup.object().required().label('Local')
      }),
      validationErrors: {},
      progress: false,
      showConfirm: false,
      types: EXPENSE_TYPES,
      currencies: [
        {
          name: 'Soles',
          code: 'PEN'
        },
        {
          name: 'Dólares',
          code: 'USD'
        }
      ]
    }
  },
  computed: {
    // El diálogo dice QUÉ cambia, campo por campo, en vez de un "¿seguro?" a
    // secas: un gasto ya pagado entra en la utilidad del local, y quien edita
    // tiene que ver el antes → después antes de confirmar.
    changesMsg () {
      const before = this.data || {}
      const after = this.document || {}
      const money = (v, cur) => Intl.NumberFormat('en-US', {
        style: 'currency', currency: (cur && cur.code) || 'PEN'
      }).format(v || 0)
      const day = v => (v ? moment(v).locale('es').format('DD/MMM/YY') : '—')
      // La observación es texto libre y puede ser larga: en el diálogo se recorta
      // para que el antes → después de los montos no quede sepultado debajo.
      const short = v => {
        const t = (v || '').trim()
        if (!t) return '—'
        return t.length > 48 ? t.slice(0, 48).trimEnd() + '…' : t
      }
      const line = (label, from, to) => label + ': ' + from + ' → ' + to
      const changes = []
      if ((before.local || {}).id !== (after.local || {}).id) {
        changes.push(line('Propiedad', (before.local || {}).description || '—', (after.local || {}).description || '—'))
      }
      if (before.type !== after.type) changes.push(line('Tipo', before.type || '—', after.type || '—'))
      if ((before.currency || {}).code !== (after.currency || {}).code) {
        changes.push(line('Moneda', (before.currency || {}).name || '—', (after.currency || {}).name || '—'))
      }
      if (Number(before.total) !== Number(after.total)) {
        changes.push(line('Monto', money(before.total, before.currency), money(after.total, after.currency)))
      }
      if (day(before.deadline) !== day(after.deadline)) {
        changes.push(line('Vencimiento', day(before.deadline), day(after.deadline)))
      }
      if ((before.observation || '') !== (after.observation || '')) {
        changes.push(line('Observación', short(before.observation), short(after.observation)))
      }
      if (!changes.length) return 'No cambiaste ningún dato del gasto. ¿Guardar de todos modos?'
      const head = before.paid
        ? 'Este gasto ya está pagado y cuenta en la utilidad del local. Se guardará — '
        : 'Se guardará — '
      return head + changes.join('. ') + '.'
    }
  },
  watch: {
    showNavbar (value) {
      if (value) {
        this.document = _.clone(this.data)
        this.showConfirm = false
        this.validationErrors = {}
        // Move calendar
        const deadline = this.$refs.deadline
        deadline.move(this.document.deadline)
      }
    }
  },
  methods: {
    async askConfirm () {
      try {
        this.validationErrors = {}
        await this.schema.validate({ ...this.document }, { abortEarly: false })
        this.showConfirm = true
      } catch (err) {
        if (err.inner) {
          err.inner.forEach(error => {
            if (error.path) this.validationErrors[error.path] = error.message
          })
        } else {
          this.$toast.error('Error al editar gasto')
        }
      }
    },
    async save () {
      try {
        this.progress = true
        this.validationErrors = {}
        await this.schema.validate({ ...this.document }, { abortEarly: false })
        const obj = {
          ...this.document,
          updatedAt: FieldValue.serverTimestamp(),
          uid: auth.currentUser.uid
        }
        await db.collection('expenses').doc(this.document.id).update({
          ...obj
        })
        this.showConfirm = false
        this.$emit('update', obj)
        this.$emit('close')
        this.$toast.success('Gasto actualizado')
      } catch (err) {
        // Si el fallo es de validación, se cierra el diálogo para que se vean
        // los mensajes de los campos, que quedan debajo.
        if (err.inner) {
          this.showConfirm = false
          err.inner.forEach(error => {
            if (error.path) {
              this.validationErrors[error.path] = error.message
            }
          })
        } else {
          this.$toast.error('Error al editar gasto')
        }
      } finally {
        this.progress = false
      }
    }
  }
}
</script>

<style scoped>

</style>
